import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeError } from "@/lib/normalizeError";
import { trackEvent } from "@/lib/analytics";

// Debug wrapper for edge function calls with raw fetch
async function invokeEdgeFunction(functionName: string, body: Record<string, unknown>) {
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, ""); // Remove trailing slash
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !anonKey) {
    throw new Error(`Missing Supabase config: URL=${!!supabaseUrl}, KEY=${!!anonKey}`);
  }

  // Check if we're in dev mode (localhost)
  const isDevMode = typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

  // Get the latest session; edge functions require a valid access token.
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error("Not signed in. Sign in to call this function.");
  }
  let accessToken = session.access_token;

  const functionUrl = `${supabaseUrl}/functions/v1/${functionName}`;

  console.log(`[EDGE INVOKE] ========================================`);
  console.log(`[EDGE INVOKE] function="${functionName}"`);
  console.log(`[EDGE INVOKE] url="${functionUrl}"`);
  console.log(`[EDGE INVOKE] isDevMode=${isDevMode}`);
  console.log(`[EDGE INVOKE] hasAccessToken=${!!accessToken}`);
  console.log(`[EDGE INVOKE] body=`, JSON.stringify(body).slice(0, 200));

  const startTime = Date.now();

  const doFetch = async (token: string) => {
    return fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": anonKey,
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  };

  // Use raw fetch for better error visibility; retry once on 401 after refreshing session
  try {
    let response = await doFetch(accessToken);

    if (response.status === 401) {
      await supabase.auth.refreshSession();
      const { data: { session: newSession } } = await supabase.auth.getSession();
      if (!newSession) {
        throw new Error("Session expired. Please sign in again.");
      }
      response = await doFetch(newSession.access_token);
    }

    console.log(`[EDGE INVOKE] Response received`);

    const elapsed = Date.now() - startTime;
    console.log(`[EDGE RESPONSE] status=${response.status} statusText="${response.statusText}" elapsed=${elapsed}ms`);

    const responseText = await response.text();
    console.log(`[EDGE RESPONSE] body (first 500 chars)=`, responseText.slice(0, 500));

    if (!response.ok) {
      console.error(`[EDGE ERROR] HTTP ${response.status}:`, responseText);
      let errMessage = responseText.slice(0, 200);
      try {
        const errBody = JSON.parse(responseText);
        if (errBody?.error && typeof errBody.error === "string") errMessage = errBody.error;
      } catch {
        // use errMessage as-is
      }
      throw new Error(errMessage);
    }

    let data;
    try {
      data = JSON.parse(responseText);
    } catch {
      throw new Error(`${functionName} returned invalid JSON: ${responseText.slice(0, 100)}`);
    }

    if (data.error) {
      console.error(`[EDGE ERROR] Error in response body:`, data.error);
      throw new Error(`${functionName}: ${data.error}`);
    }

    console.log(`[EDGE SUCCESS] function="${functionName}" elapsed=${elapsed}ms`);
    console.log(`[EDGE INVOKE] ========================================`);
    return { data, error: null };
  } catch (err: any) {
    const elapsed = Date.now() - startTime;
    console.error(`[EDGE EXCEPTION] function="${functionName}" elapsed=${elapsed}ms`);
    console.error(`[EDGE EXCEPTION] name="${err.name}" message="${err.message}"`);

    // Check for CORS or network errors
    if (err.name === "TypeError") {
      console.error(`[EDGE EXCEPTION] Likely CORS or network error`);
    }

    throw err;
  }
}

interface StartupData {
  title?: string;
  tagline?: string;
  problem?: string;
  solution?: string;
  pricing?: any;
  features?: string[];
}

export function useCreateProjectFromStartup() {
  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState<string>("");
  const navigate = useNavigate();
  const { user } = useAuth();

  const createProjectFromIdea = async (ideaId: string, options?: { prefill?: string }) => {
    if (!user) {
      toast.error("Please sign in to build this app");
      navigate(`/auth?next=${encodeURIComponent(`/idea/${ideaId}`)}`);
      return null;
    }

    setIsCreating(true);
    setProgress("Creating project...");
    
    try {
      // First fetch the idea details for scaffold generation
      const { data: idea, error: ideaFetchError } = await supabase
        .from("ideas")
        .select("title, tagline, problem, solution, description, tags")
        .eq("id", ideaId)
        .single();

      if (ideaFetchError) {
        console.warn("Could not fetch idea details:", ideaFetchError);
      }

      setProgress("Generating app structure with AI...");

      // Call the create-project endpoint which now includes scaffold generation
      const { data, error } = await supabase.functions.invoke("create-project-from-startup", {
        body: { 
          ideaId,
          generateScaffold: true,
          startupData: idea ? {
            title: idea.title,
            tagline: idea.tagline,
            problem: idea.problem,
            solution: idea.solution,
            features: idea.tags,
          } : undefined,
        },
      });

      if (error) throw error;

      console.log('[useCreateProjectFromStartup] CREATE_PROJECT_RESULT:', data);
      
      if (data?.projectId) {
        console.log('[useCreateProjectFromStartup] ROUTING_TO_PROJECT_ID:', data.projectId);
        
        // Verify the project is readable before navigating (minimal select to avoid schema cache issues)
        const { data: verifyData, error: verifyError } = await supabase
          .from('code_projects')
          .select('id')
          .eq('id', data.projectId)
          .maybeSingle();
        
        console.log('[useCreateProjectFromStartup] VERIFY_PROJECT_READABLE:', { verifyData, verifyError });
        
        if (verifyError || !verifyData) {
          console.error('[useCreateProjectFromStartup] Project created but not readable:', verifyError);
          const verifyMsg = verifyError?.message ?? "Not accessible";
          toast.error(verifyMsg);
          navigate('/my-apps');
          return null;
        }
        
        setProgress("Opening builder...");
        toast.success(`Project created with ${data.fileCount || "multiple"} files!`);
        const prefill = (options?.prefill ?? "").trim();
        navigate(`/code-workspace?projectId=${data.projectId}`, { state: prefill ? { autoBuild: false, prefill, source: "idea" } : { autoBuild: false, source: "idea" } });
        return data.projectId;
      } else {
        console.error('[useCreateProjectFromStartup] No projectId in response:', data);
        throw new Error("No project ID returned");
      }
    } catch (err) {
      const e = normalizeError(err);
      console.error("[useCreateProjectFromStartup] Error (createProjectFromIdea):", e.name, e.message, e.raw);
      toast.error(e.message ?? "Failed to create project. Please try again.");
      return null;
    } finally {
      setIsCreating(false);
      setProgress("");
    }
  };

  const createProjectFromPrompt = async (prompt: string, options?: { prefill?: string }) => {
    if (!user) {
      toast.error("Please sign in first");
      navigate("/auth");
      return null;
    }
    console.log(`[useCreateProjectFromStartup] userId=${user.id}`);

    setIsCreating(true);
    setProgress("Analyzing your idea...");
    trackEvent('build_started');

    try {
      // Create a project directly from prompt (skip idea generation for speed)
      setProgress("Creating project structure...");

      // Create the project first (with debug logging)
      const { data: projectData } = await invokeEdgeFunction("code-workspace-files", {
        action: "createProject",
        name: extractProjectName(prompt),
        description: prompt,
      });

      const projectId = projectData?.project?.id;
      console.log('[useCreateProjectFromStartup] CREATE_PROJECT_RESULT:', projectData?.project);

      if (!projectId) {
        console.error('[useCreateProjectFromStartup] Project created but id missing');
        throw new Error("Project created but id missing; build cannot continue.");
      }

      console.log('[useCreateProjectFromStartup] PROJECT_ID:', projectId);

      setProgress("Generating app with AI...");

      const { enhancedPrompt, appKind } = enhancePromptWithAppKind(prompt);

      // Generate scaffold with AI - THIS IS THE COMMIT STEP
      // Files are saved to Supabase Storage inside generate-app-scaffold
      const { data: scaffoldData } = await invokeEdgeFunction("generate-app-scaffold", {
        projectId,
        userId: user.id,
        prompt: enhancedPrompt,
        appKind, // optional hint for future backend use
      });

      const fileCount = scaffoldData?.fileCount || (scaffoldData?.files ? Object.keys(scaffoldData.files).length : 0);
      const filePaths = scaffoldData?.files ? Object.keys(scaffoldData.files) : [];
      console.log(`[SCAFFOLD COMMITTED] projectId=${projectId} fileCount=${fileCount} paths=`, filePaths.slice(0, 10));

      // REQUIRED: Verify scaffold was committed with sufficient files
      // Must have at least 4 files (index.html, src/main.tsx, src/App.tsx, + 1 page/component)
      if (fileCount < 4) {
        console.error(`[useCreateProjectFromStartup] Scaffold commit incomplete: only ${fileCount} files`);
        toast.error(`Scaffold generation incomplete: only ${fileCount} files generated.`);
        return null;
      }

      // Verify the project is readable before navigating (minimal select to avoid schema cache issues)
      const { data: verifyData, error: verifyError } = await supabase
        .from('code_projects')
        .select('id')
        .eq('id', projectId)
        .maybeSingle();

      console.log('[useCreateProjectFromStartup] VERIFY_PROJECT_READABLE:', { verifyData, verifyError });

      if (verifyError || !verifyData) {
        console.error('[useCreateProjectFromStartup] Project created but not readable:', verifyError);
        const verifyMsg = verifyError?.message ?? "Not accessible";
        toast.error(verifyMsg);
        navigate('/my-apps');
        return null;
      }

      setProgress("Opening builder...");
      trackEvent('build_completed');
      toast.success(`Project created with ${fileCount} files!`);
      const prefill = (options?.prefill ?? prompt ?? "").trim();
      navigate(`/code-workspace?projectId=${projectId}`, { state: { autoBuild: false, prefill, source: "create" } });
      return projectId;
    } catch (err) {
      const e = normalizeError(err);
      console.error("[useCreateProjectFromStartup] Error (createProjectFromPrompt):", e.name, e.message, e.raw);
      toast.error(e.message ?? "Failed to create project from prompt.");
      return null;
    } finally {
      setIsCreating(false);
      setProgress("");
    }
  };

  /**
   * Creates a project with scaffold files, inserts a build_plans row, then navigates to /plan/:projectId.
   * Scaffold is generated BEFORE plan mode so CodeWorkspace can boot.
   */
  const createProjectFromPromptWithPlanMode = async (
    prompt: string,
    options?: { prefill?: string }
  ) => {
    // Feature flag: allow disabling plan mode in emergency without rollback
    const planModeEnabled = import.meta.env.VITE_PLAN_MODE_ENABLED !== "false";
    if (!planModeEnabled) {
      console.log("[createProjectFromPromptWithPlanMode] Plan mode disabled via flag, falling back to direct build");
      return createProjectFromPrompt(prompt, options);
    }

    if (isCreating) return;
    setIsCreating(true);
    setProgress("Creating project...");

    try {
      if (!user) {
        toast.error("Please sign in to create a project");
        navigate("/auth");
        return;
      }

      // Step 1: Create project
      const { data: projectData, error: createError } = await invokeEdgeFunction("code-workspace-files", {
        action: "createProject",
        name: extractProjectName(prompt),
        description: prompt,
      });

      if (createError) throw new Error(`Failed to create project: ${createError.message}`);
      const projectId = projectData?.project?.id;
      if (!projectId) throw new Error("No projectId returned from code-workspace-files");

      setProgress("Setting up framework...");

      // Step 2: Generate scaffold with AI (REQUIRED for CodeWorkspace to boot)
      const { enhancedPrompt, appKind } = enhancePromptWithAppKind(prompt);

      const { data: scaffoldData } = await invokeEdgeFunction("generate-app-scaffold", {
        projectId,
        userId: user.id,
        prompt: enhancedPrompt,
        appKind,
      });

      const fileCount = scaffoldData?.fileCount || (scaffoldData?.files ? Object.keys(scaffoldData.files).length : 0);
      const filePaths = scaffoldData?.files ? Object.keys(scaffoldData.files) : [];
      console.log(`[SCAFFOLD COMMITTED] projectId=${projectId} fileCount=${fileCount} paths=`, filePaths.slice(0, 10));

      // Verify scaffold has minimum files
      if (fileCount < 4) {
        console.error(`[createProjectFromPromptWithPlanMode] Scaffold incomplete: only ${fileCount} files`);
        // Cleanup orphan project
        await supabase.from("code_projects").delete().eq("id", projectId);
        throw new Error(`Scaffold generation incomplete: only ${fileCount} files generated.`);
      }

      setProgress("Setting up plan mode...");

      // Step 3: Upsert build_plans row with status 'draft'
      const { error: insertError } = await supabase
        .from("build_plans")
        .upsert({
          project_id: projectId,
          user_id: user.id,
          prompt: prompt,
          plan_status: "draft",
          clarifying_questions: [],
          plan_text: null,
          approved_at: null,
        }, { onConflict: "project_id" });

      if (insertError) {
        console.error("[createProjectFromPromptWithPlanMode] build_plans insert failed:", insertError);
        // Cleanup: delete the project (cascades to scaffold files via FK)
        await supabase.from("code_projects").delete().eq("id", projectId);
        throw new Error(`Failed to create build plan: ${insertError.message}`);
      }

      // Step 4: Navigate to plan mode
      navigate(`/plan/${projectId}`);
    } catch (error) {
      console.error("[createProjectFromPromptWithPlanMode] error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create project");
    } finally {
      setIsCreating(false);
      setProgress("");
    }
  };

  return {
    createProjectFromIdea,
    createProjectFromPrompt,
    createProjectFromPromptWithPlanMode,
    isCreating,
    progress,
  };
}

function extractProjectName(prompt: string): string {
  // Extract a meaningful project name from the prompt, skipping common verbs and stopwords
  const STOP = new Set(["a", "an", "the", "for", "and", "with", "of", "to", "in", "on", "my", "me",
    "build", "create", "make", "design", "develop", "generate", "write", "code",
    "app", "application", "website", "site", "page", "web", "simple", "basic", "full"]);
  const words = prompt.split(/\s+/).slice(0, 8);
  const cleaned = words
    .filter(w => w.length > 1 && !STOP.has(w.toLowerCase()))
    .slice(0, 3)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
  return cleaned || "New Project";
}

/** Heuristics: avoid "landing page default" for app-like prompts. Enhances prompt for backend; backend may use appKind when supported. */
const APP_LIKE_KEYWORDS = /app|dashboard|tracker|platform|tool|builder|CRM|SaaS|manager|admin|portal|workspace/i;
const LANDING_LIKE_KEYWORDS = /landing|marketing|homepage|site\s*only|single\s*page\s*site|promo\s*page/i;

function enhancePromptWithAppKind(userPrompt: string): { enhancedPrompt: string; appKind: "multi_page_app" | "landing" } {
  const lower = userPrompt.trim().toLowerCase();
  const isLanding = LANDING_LIKE_KEYWORDS.test(lower);
  const isApp = APP_LIKE_KEYWORDS.test(lower);

  if (isLanding) {
    return {
      enhancedPrompt: userPrompt + "\n\nGenerate a marketing/landing page using the existing Landing Kit components (Hero, Features, Testimonials, Pricing, Footer). Single-page site.",
      appKind: "landing",
    };
  }
  if (isApp) {
    return {
      enhancedPrompt: userPrompt + "\n\nGenerate a functional multi-page React app (not a single landing page). Include: React Router with at least 2–4 screens (e.g. Home, List, Detail, Settings or similar). Use localStorage for persistence unless the user asked for auth or a database. Include simple CRUD-style flows where relevant. Use only @/components/ui and existing design system; no extra npm packages.",
      appKind: "multi_page_app",
    };
  }
  return { enhancedPrompt: userPrompt, appKind: "multi_page_app" };
}
